// src/components/admin/AdminWheel.js
import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../lib/AppContext';

const TYPE_OPTIONS = [
  { value: 'percent',      label: '% Percentage Off' },
  { value: 'fixed',        label: '€ Fixed Amount Off' },
  { value: 'free_shipping',label: '📦 Free Shipping' },
  { value: 'spins',        label: '🎰 Extra Spins' },
];

const COLORS = [
  '#4cca74','#3bb85e','#2da050','#f0a500',
  '#4a9edd','#e05050','#9b59b6','#e67e22','#1abc9c',
];

const EMPTY_REWARD = {
  label: '', type: 'percent', value: '', weight: 10,
  color: '#4cca74', emoji: '🎁', active: true, sort_order: 0,
};

function RewardForm({ initial, totalWeight, onSave, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY_REWARD);
  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const myWeight = parseInt(form.weight) || 0;
  const othersWeight = totalWeight - (parseInt(initial?.weight) || 0);
  const newTotal = othersWeight + myWeight;
  const approxPct = newTotal > 0 ? ((myWeight / newTotal) * 100).toFixed(1) : '0';

  return (
    <div style={{
      background: '#0d0d0d', border: '1px solid rgba(76,202,116,.2)',
      borderRadius: 16, padding: 24, marginBottom: 16,
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14 }}>
        {/* Label */}
        <div style={{ gridColumn: '1 / -1' }}>
          <div className="input-label">Segment Label</div>
          <input className="input-field" value={form.label} onChange={e => up('label', e.target.value)} placeholder="10% Off" />
        </div>

        {/* Type */}
        <div>
          <div className="input-label">Reward Type</div>
          <select className="input-field" value={form.type} onChange={e => up('type', e.target.value)} style={{ appearance: 'none' }}>
            {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* Value */}
        {form.type !== 'free_shipping' && (
          <div>
            <div className="input-label">{form.type === 'percent' ? 'Discount %' : form.type === 'spins' ? 'Spins to Add' : 'Amount (€)'}</div>
            <input className="input-field" type="number" min="0" value={form.value}
              onChange={e => up('value', e.target.value)} placeholder="10" />
          </div>
        )}

        {/* Weight */}
        <div>
          <div className="input-label">
            Weight ({approxPct}% probability)
            <span style={{ color: '#555', fontSize: '.72rem', marginLeft: 6 }}>Higher = more common</span>
          </div>
          <input className="input-field" type="number" min="1" value={form.weight}
            onChange={e => up('weight', e.target.value)} />
          <input type="range" min="1" max="100" value={form.weight}
            onChange={e => up('weight', e.target.value)}
            style={{ width: '100%', marginTop: 6, accentColor: '#4cca74' }} />
        </div>

        {/* Emoji */}
        <div>
          <div className="input-label">Emoji</div>
          <input className="input-field" value={form.emoji} onChange={e => up('emoji', e.target.value)}
            style={{ fontSize: '1.2rem', textAlign: 'center' }} maxLength={2} />
        </div>

        {/* Sort order */}
        <div>
          <div className="input-label">Sort Order</div>
          <input className="input-field" type="number" min="0" value={form.sort_order}
            onChange={e => up('sort_order', e.target.value)} />
        </div>

        {/* Color */}
        <div>
          <div className="input-label">Segment Color</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
            {COLORS.map(c => (
              <button key={c} type="button" onClick={() => up('color', c)} style={{
                width: 28, height: 28, borderRadius: '50%', background: c,
                border: form.color === c ? '3px solid #fff' : '2px solid transparent',
                cursor: 'pointer', flexShrink: 0,
              }} />
            ))}
            <input type="color" value={form.color} onChange={e => up('color', e.target.value)}
              style={{ width: 28, height: 28, border: 'none', background: 'none', cursor: 'pointer', padding: 0 }} />
          </div>
        </div>

        {/* Active */}
        <div style={{ display: 'flex', alignItems: 'center', paddingTop: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.active} onChange={e => up('active', e.target.checked)}
              style={{ accentColor: '#4cca74', width: 16, height: 16 }} />
            <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.82rem', color: '#ccc' }}>Active on wheel</span>
          </label>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
        <button className="btn-primary" onClick={() => onSave(form)} style={{ minHeight: 44 }}>Save Reward</button>
        <button onClick={onCancel} style={{
          background: 'none', border: '1px solid rgba(255,255,255,.1)', borderRadius: 25,
          padding: '10px 20px', color: '#555', fontFamily: "'Barlow Condensed', sans-serif",
          fontSize: '.85rem', fontWeight: 700, cursor: 'pointer', minHeight: 44,
        }}>Cancel</button>
      </div>
    </div>
  );
}

export default function AdminWheel() {
  const { showToast } = useApp();
  const [rewards, setRewards]   = useState([]);
  const [settings, setSettings] = useState(null);
  const [editing, setEditing]   = useState(null);
  const [loading, setLoading]   = useState(true);

  const load = async () => {
    const [r, s] = await Promise.all([
      supabase.from('wheel_rewards').select('*').order('sort_order'),
      supabase.from('wheel_settings').select('*').eq('id', 1).single(),
    ]);
    setRewards(r.data || []);
    setSettings(s.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const totalWeight = rewards.reduce((s, r) => s + (r.weight || 0), 0);

  const saveReward = async (form) => {
    if (!form.label) { showToast('Label required', 'error'); return; }
    const payload = {
      label:      form.label,
      type:       form.type,
      value:      form.type === 'free_shipping' ? 0 : parseFloat(form.value) || 0,
      weight:     parseInt(form.weight) || 10,
      color:      form.color,
      emoji:      form.emoji,
      active:     form.active,
      sort_order: parseInt(form.sort_order) || 0,
    };
    let err;
    if (editing === 'new') {
      ({ error: err } = await supabase.from('wheel_rewards').insert(payload));
    } else {
      ({ error: err } = await supabase.from('wheel_rewards').update(payload).eq('id', editing.id));
    }
    if (err) { showToast('Save failed: ' + err.message, 'error'); return; }
    showToast('Reward saved', 'success');
    setEditing(null);
    load();
  };

  const deleteReward = async (id) => {
    if (!window.confirm('Delete this wheel segment?')) return;
    await supabase.from('wheel_rewards').delete().eq('id', id);
    showToast('Segment deleted', 'info');
    load();
  };

  const toggleReward = async (r) => {
    await supabase.from('wheel_rewards').update({ active: !r.active }).eq('id', r.id);
    load();
  };

  const saveSettings = async (patch) => {
    await supabase.from('wheel_settings').update({ ...patch, updated_at: new Date().toISOString() }).eq('id', 1);
    setSettings(s => ({ ...s, ...patch }));
    showToast('Settings saved', 'success');
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}><div className="spinner" /></div>;

  const activeRewards = rewards.filter(r => r.active);
  const activeWeight  = activeRewards.reduce((s, r) => s + r.weight, 0);

  return (
    <div className="page-container" style={{ padding: '32px 24px', maxWidth: 900 }}>
      <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.8rem', letterSpacing: '.05em', marginBottom: 4 }}>
        Wheel Management
      </h2>
      <p style={{ fontSize: '.8rem', color: '#555', marginBottom: 24 }}>
        Configure the reward wheel segments and probabilities. Changes apply instantly.
      </p>

      {/* Global settings card */}
      {settings && (
        <div style={{ background: '#111', border: '1px solid rgba(255,255,255,.07)', borderRadius: 16, padding: 20, marginBottom: 24 }}>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.7rem', fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: '#4cca74', marginBottom: 14 }}>
            Global Settings
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={settings.enabled}
                onChange={e => saveSettings({ enabled: e.target.checked })}
                style={{ accentColor: '#4cca74', width: 18, height: 18 }} />
              <div>
                <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.88rem', fontWeight: 700, color: settings.enabled ? '#4cca74' : '#e05050' }}>
                  Wheel {settings.enabled ? 'ENABLED' : 'DISABLED'}
                </div>
                <div style={{ fontSize: '.75rem', color: '#555' }}>Toggle to temporarily suspend the wheel</div>
              </div>
            </label>
            <div style={{ fontSize: '.8rem', color: '#555' }}>
              {activeRewards.length} active segments · Total weight: {activeWeight}
            </div>
          </div>
        </div>
      )}

      {/* Probability visual */}
      {activeRewards.length > 0 && (
        <div style={{ background: '#111', border: '1px solid rgba(255,255,255,.07)', borderRadius: 16, padding: 20, marginBottom: 24 }}>
          <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.7rem', fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: '#4cca74', marginBottom: 14 }}>
            Probability Distribution
          </div>
          {activeRewards.map(r => {
            const pct = activeWeight > 0 ? ((r.weight / activeWeight) * 100) : 0;
            return (
              <div key={r.id} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontSize: '.8rem', color: '#ccc' }}>{r.emoji} {r.label}</span>
                  <span style={{ fontSize: '.78rem', color: '#4cca74', fontWeight: 700 }}>{pct.toFixed(1)}%</span>
                </div>
                <div style={{ height: 6, background: '#1a1a1a', borderRadius: 3 }}>
                  <div style={{ height: '100%', background: r.color, borderRadius: 3, width: `${pct}%`, transition: 'width .4s' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        {!editing && (
          <button className="btn-primary" onClick={() => setEditing('new')} style={{ minHeight: 44 }}>
            + Add Segment
          </button>
        )}
      </div>

      {editing === 'new' && (
        <RewardForm initial={EMPTY_REWARD} totalWeight={totalWeight} onSave={saveReward} onCancel={() => setEditing(null)} />
      )}

      {/* Reward list */}
      <div style={{ background: '#111', borderRadius: 16, border: '1px solid rgba(255,255,255,.07)', overflow: 'hidden' }}>
        {rewards.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#444' }}>
            No segments yet. Add one above.
          </div>
        )}
        {rewards.map(r => (
          <div key={r.id}>
            {editing && editing !== 'new' && editing.id === r.id ? (
              <RewardForm initial={r} totalWeight={totalWeight} onSave={saveReward} onCancel={() => setEditing(null)} />
            ) : (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px',
                borderBottom: '1px solid rgba(255,255,255,.04)',
                opacity: r.active ? 1 : .45,
              }}>
                {/* Color swatch */}
                <div style={{ width: 36, height: 36, borderRadius: 10, background: r.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>
                  {r.emoji}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.9rem', fontWeight: 700, color: '#f4f4f4', marginBottom: 2 }}>
                    {r.label}
                  </div>
                  <div style={{ fontSize: '.75rem', color: '#555', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <span>{TYPE_OPTIONS.find(o => o.value === r.type)?.label}</span>
                    {r.type !== 'free_shipping' && <span style={{ color: '#4cca74', fontWeight: 700 }}>
                      {r.type === 'percent' ? `${r.value}%` : r.type === 'spins' ? `+${r.value} spin` : `€${r.value}`}
                    </span>}
                    <span>Weight: {r.weight} ({activeWeight > 0 ? ((r.weight / activeWeight) * 100).toFixed(1) : 0}%)</span>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button onClick={() => toggleReward(r)} style={{
                    background: 'none', border: `1px solid ${r.active ? 'rgba(240,165,0,.3)' : 'rgba(76,202,116,.3)'}`,
                    borderRadius: 20, padding: '6px 12px', color: r.active ? '#f0a500' : '#4cca74',
                    fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.72rem', fontWeight: 700, cursor: 'pointer', minHeight: 36,
                  }}>{r.active ? 'Hide' : 'Show'}</button>
                  <button onClick={() => setEditing(r)} style={{
                    background: 'none', border: '1px solid rgba(255,255,255,.1)',
                    borderRadius: 20, padding: '6px 12px', color: '#888',
                    fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.72rem', fontWeight: 700, cursor: 'pointer', minHeight: 36,
                  }}>Edit</button>
                  <button onClick={() => deleteReward(r.id)} style={{
                    background: 'none', border: '1px solid rgba(224,80,80,.25)',
                    borderRadius: 20, padding: '6px 12px', color: '#e05050',
                    fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.72rem', fontWeight: 700, cursor: 'pointer', minHeight: 36,
                  }}>Delete</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
